import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/layout/AdminLayoutNew';
import { RefreshCw, Link2, Clock, CheckCircle, AlertCircle, Database, Map, Layers } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminSync() {
  const { getAuthHeaders } = useAuth();
  const [settings, setSettings] = useState(null);
  const [status, setStatus] = useState(null);
  const [sheetUrl, setSheetUrl] = useState('');
  const [autoSync, setAutoSync] = useState(false);
  const [syncInterval, setSyncInterval] = useState(1);
  const [syncing, setSyncing] = useState(false);
  const [savingPositions, setSavingPositions] = useState(false);
  const [loading, setLoading] = useState(true);

  const projectId = 'bakrajo-hills';

  const fetchData = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const [settingsRes, statusRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/sync/settings/${projectId}`, { headers }),
        axios.get(`${API_URL}/api/admin/sync/status/${projectId}`, { headers }),
      ]);
      setSettings(settingsRes.data);
      setStatus(statusRes.data);
      setSheetUrl(settingsRes.data.google_sheet_url || '');
      setAutoSync(settingsRes.data.auto_sync_enabled || false);
      setSyncInterval(settingsRes.data.sync_interval_hours || 1);
    } catch (error) {
      console.error('Error fetching sync data:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaveSettings = async () => {
    try {
      await axios.put(
        `${API_URL}/api/admin/sync/settings/${projectId}`,
        { google_sheet_url: sheetUrl, auto_sync_enabled: autoSync, sync_interval_hours: syncInterval },
        { headers: getAuthHeaders() }
      );
      toast.success('Sync settings saved');
      fetchData();
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const handleTriggerSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/sync/trigger/${projectId}`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleSavePositions = async () => {
    setSavingPositions(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/map-positions/save/${projectId}`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to save positions');
    } finally {
      setSavingPositions(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-8 max-w-4xl" data-testid="admin-sync-page">
        <div>
          <h1 className="text-2xl font-light text-white tracking-tight">Google Sheets Sync</h1>
          <p className="text-sm text-white/40 mt-1">Connect a Google Sheet to auto-sync unit data. Positions are preserved.</p>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-white/5 border border-white/10 p-4 space-y-1">
            <div className="flex items-center gap-2 text-white/40">
              <Database className="w-4 h-4" />
              <span className="text-[10px] uppercase tracking-wider">Total Units</span>
            </div>
            <p className="text-2xl font-light text-white" data-testid="sync-total-units">{status?.total || 0}</p>
          </div>
          <div className="bg-white/5 border border-white/10 p-4 space-y-1">
            <div className="flex items-center gap-2 text-white/40">
              <Map className="w-4 h-4" />
              <span className="text-[10px] uppercase tracking-wider">On Map</span>
            </div>
            <p className="text-2xl font-light text-white" data-testid="sync-on-map">{status?.on_map || 0}</p>
          </div>
          <div className="bg-white/5 border border-white/10 p-4 space-y-1">
            <div className="flex items-center gap-2 text-green-400/60">
              <Layers className="w-4 h-4" />
              <span className="text-[10px] uppercase tracking-wider">Zone 1</span>
            </div>
            <p className="text-2xl font-light text-green-400" data-testid="sync-zone1">{status?.zone_1 || 0}</p>
          </div>
          <div className="bg-white/5 border border-white/10 p-4 space-y-1">
            <div className="flex items-center gap-2 text-blue-400/60">
              <Layers className="w-4 h-4" />
              <span className="text-[10px] uppercase tracking-wider">Zone 2</span>
            </div>
            <p className="text-2xl font-light text-blue-400" data-testid="sync-zone2">{status?.zone_2 || 0}</p>
          </div>
        </div>

        {/* Google Sheets Settings */}
        <div className="bg-white/5 border border-white/10 p-6 space-y-5">
          <div className="flex items-center gap-3">
            <Link2 className="w-5 h-5 text-[#D4AF37]" />
            <h2 className="text-lg font-light text-white">Sheet Connection</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-xs text-white/40 uppercase tracking-wider mb-2">Google Sheet URL</label>
              <input
                type="url"
                value={sheetUrl}
                onChange={(e) => setSheetUrl(e.target.value)}
                placeholder="https://docs.google.com/spreadsheets/d/..."
                className="w-full bg-black border border-white/10 text-white px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none transition-colors"
                data-testid="sync-sheet-url-input"
              />
              <p className="text-[10px] text-white/30 mt-1">Sheet must be publicly viewable ("Anyone with the link can view"). Tabs must be named: TYPE A 300m², TYPE B 203m², TYPE C 160m²</p>
            </div>

            <div className="flex items-center gap-6">
              <label className="flex items-center gap-3 cursor-pointer">
                <div
                  className={`w-10 h-5 rounded-full relative transition-colors cursor-pointer ${autoSync ? 'bg-[#D4AF37]' : 'bg-white/10'}`}
                  onClick={() => setAutoSync(!autoSync)}
                  data-testid="sync-auto-toggle"
                >
                  <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${autoSync ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </div>
                <span className="text-sm text-white/70">Auto-sync</span>
              </label>

              {autoSync && (
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-white/40" />
                  <select
                    value={syncInterval}
                    onChange={(e) => setSyncInterval(Number(e.target.value))}
                    className="bg-black border border-white/10 text-white text-sm px-3 py-1.5 focus:outline-none"
                    data-testid="sync-interval-select"
                  >
                    <option value={1}>Every 1 hour</option>
                    <option value={3}>Every 3 hours</option>
                    <option value={6}>Every 6 hours</option>
                    <option value={12}>Every 12 hours</option>
                    <option value={24}>Every 24 hours</option>
                  </select>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={handleSaveSettings}
                className="px-6 py-2.5 bg-white text-black text-xs uppercase tracking-wider hover:bg-white/90 transition-colors"
                data-testid="sync-save-settings-btn"
              >
                Save Settings
              </button>
              <button
                onClick={handleTriggerSync}
                disabled={syncing || !sheetUrl}
                className="px-6 py-2.5 bg-[#D4AF37] text-black text-xs uppercase tracking-wider hover:bg-[#D4AF37]/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                data-testid="sync-trigger-btn"
              >
                <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Syncing...' : 'Sync Now'}
              </button>
            </div>
          </div>

          {status?.last_synced && (
            <div className="flex items-center gap-2 text-xs text-white/30 pt-2 border-t border-white/5">
              <CheckCircle className="w-3 h-3" />
              <span>Last synced: {new Date(status.last_synced).toLocaleString()}</span>
            </div>
          )}
        </div>

        {/* Persistent Position Cache */}
        <div className="bg-white/5 border border-white/10 p-6 space-y-5">
          <div className="flex items-center gap-3">
            <Map className="w-5 h-5 text-[#D4AF37]" />
            <h2 className="text-lg font-light text-white">Position Cache</h2>
          </div>
          <p className="text-sm text-white/40">
            Stored positions: <span className="text-white font-medium">{status?.positions_stored || 0}</span> units.
            These are used to auto-place new units from Google Sheets without re-running AI detection.
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={handleSavePositions}
              disabled={savingPositions}
              className="px-6 py-2.5 border border-white/20 text-white text-xs uppercase tracking-wider hover:bg-white/5 transition-colors disabled:opacity-40 flex items-center gap-2"
              data-testid="sync-save-positions-btn"
            >
              <Database className={`w-4 h-4 ${savingPositions ? 'animate-spin' : ''}`} />
              {savingPositions ? 'Saving...' : 'Save Current Positions to Cache'}
            </button>
          </div>
          <div className="flex items-center gap-2 text-xs text-white/30">
            <AlertCircle className="w-3 h-3" />
            <span>Run this after manually adjusting positions in the Map Editor to update the cache.</span>
          </div>
        </div>

        {/* Zone Info */}
        <div className="bg-white/5 border border-white/10 p-6 space-y-4">
          <div className="flex items-center gap-3">
            <Layers className="w-5 h-5 text-[#D4AF37]" />
            <h2 className="text-lg font-light text-white">Zone Configuration</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="border border-green-500/20 bg-green-500/5 p-4 space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span className="text-sm text-white font-medium">Zone 1 — Active</span>
              </div>
              <p className="text-xs text-white/40">Below the road. {status?.zone_1 || 0} units. Currently serving buyers.</p>
            </div>
            <div className="border border-blue-500/20 bg-blue-500/5 p-4 space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-500" />
                <span className="text-sm text-white font-medium">Zone 2 — Future</span>
              </div>
              <p className="text-xs text-white/40">Above the road. {status?.zone_2 || 0} units. Reserved for future development.</p>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
